import classNames from 'classnames';
import {
  Button,
  TextField,
  IconButton,
  Typography,
  Checkbox,
  XIcon2,
} from 'fasoo-ui-component-next';
import React, { useEffect, useState, useRef } from 'react';

import { useTranslation, Trans } from 'react-i18next';
import { CommonProps } from 'src/components/helpers/props';

import './index.scss';
import './LoginForm.scss';

export interface LoginState {
  userId: string;
  password: string;
}

export interface LoginErrorState {
  name: string;
  message: string;
}

export interface IProps extends CommonProps {
  handleSignIn: (userId: string, password: string) => void;
  loginResult?: boolean;
  loginError?: LoginErrorState;
  checkSaveUserId: (isRemain: boolean) => void;
  handleSetPasswordInit?: (userId: string) => Promise<string>;
  initPWResult?: string;
  userIdInputRef: React.RefObject<HTMLInputElement>;
  passwordInputRef: React.RefObject<HTMLInputElement>;
  submitButtonRef: React.RefObject<HTMLButtonElement>;
}

const LoginForm: React.FC<IProps> = ({
  handleSignIn,
  loginResult = true,
  checkSaveUserId,
  handleSetPasswordInit,
  initPWResult,
  loginError,
  userIdInputRef,
  passwordInputRef,
  submitButtonRef,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [init, setInit] = useState<boolean>(false);

  const loginId = window.localStorage.getItem('loginId')
    ? window.localStorage.getItem('loginId')
    : undefined;
  const [state, setState] = useState<LoginState>({
    userId: window.localStorage.getItem('saveUserId') === 'true' && loginId ? loginId : '',
    password: '',
  });
  const [errstate, setErrState] = useState({
    isError: false,
    errType: '',
    errMsg: '',
  });
  const [remainID] = useState(window.localStorage.getItem('saveUserId') === 'true' ? true : false);
  const handleChangeInputEvt = (name: string, value: string): void => {
    let newValue = value;

    if (name === 'userId') {
      if (value === '') {
        setState({
          ...state,
          userId: '',
        });
        return;
      }
      if (value.length > 60) {
        newValue = value.substr(0, 60);
        return;
      }
      if (!RegExp(/^[-_.@0-9A-Za-zㄱ-ㅎㅏ-ㅣ가-힣]+$/).test(value)) {
        return;
      }
    } else {
      if (value === '') {
        setState({
          ...state,
          password: '',
        });
        return;
      }
      if (value.length > 250) {
        newValue = value.substr(0, 250);
        return;
      }
    }
    setState({
      ...state,
      [name]: newValue,
    });
  };

  const submitSignIn = (): boolean => {
    userIdInputRef.current?.blur();
    passwordInputRef.current?.blur();
    submitButtonRef.current?.blur();

    if (state.userId === '') {
      setErrState({
        isError: true,
        errType: 'MissingUserId',
        errMsg: t('login.enterIdorMail'),
      });
      userIdInputRef.current?.focus();
      return false;
    } else if (state.password === '') {
      setErrState({
        isError: true,
        errType: 'MissingPassword',
        errMsg: t('login.enterPassword'),
      });
      userIdInputRef.current?.focus();
      return false;
    } else {
      setErrState({
        ...errstate,
        isError: false,
      });
      handleSignIn(state.userId, state.password);
      checkSaveUserId(remainID);
      return true;
    }
  };

  const { t } = useTranslation(['commons']);
  // const [isAutoLogin, setIsAutoLogin] = useState<boolean>(false);

  // 최초 포커스를 ID로
  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  return (
    <form className={'login-form'}>
      <Typography variant="h3" classes="mg mt-24 login-form__label">
        {t('commons:login.formLabel')}
      </Typography>
      <Typography variant="p" classes="mg mt-28 text-field__label">
        {t('commons:login.id')}
      </Typography>
      <TextField
        fullWidth={true}
        variant="standard"
        error={errstate.isError === true && errstate.errType === 'MissingUserId'}
        placeholder={t('commons:login.message.emptyId')}
        value={state.userId}
        // endIcon={<PreviewIcon />}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            submitSignIn();
          }
        }}
        inputRef={userIdInputRef}
        endIcon={
          state.userId && (
            <IconButton
              onClick={() => {
                handleChangeInputEvt('userId', '');
              }}
              tabIndex={-1}
              circle
              color="secondary"
              classes="input-value__remove-button"
            >
              <XIcon2 size="sm" />
            </IconButton>
          )
        }
        onChange={(e) => handleChangeInputEvt('userId', e.target.value)}
        maxLength={64}
      />
      <Typography variant="p" classes="mg mt-28 text-field__label">
        {t('commons:login.password')}
      </Typography>
      <TextField
        type="password"
        fullWidth={true}
        variant="standard"
        error={errstate.isError === true && errstate.errType === 'MissingPassword'}
        placeholder={t('commons:login.message.emptyPassword')}
        value={state.password}
        inputRef={passwordInputRef}
        endIcon={
          state.password && (
            <IconButton
              onClick={() => handleChangeInputEvt('password', '')}
              tabIndex={-1}
              circle
              color="secondary"
              classes="input-value__remove-button"
            >
              <XIcon2 size="sm" />
            </IconButton>
          )
        }
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            submitSignIn();
          }
        }}
        onChange={(e) => handleChangeInputEvt('password', e.target.value)}
      />
      {/* <Checkbox
        label="[]자동 로그인"
        radio
        checked={isAutoLogin}
        onChange={() => {
          setIsAutoLogin(!isAutoLogin);
        }}
        classes="auto-login__checkbox mg mt-20"
      /> */}
      {!loginResult && loginError && loginError.name === 'INVALID_USER_ID_PWD' ? (
        <div className="login-fail__message mg mt-12">{t('commons:login.message.notMatch')}</div>
      ) : !loginResult && loginError && loginError.name === 'NO_APPROVER' ? (
        <div className="login-fail__message mg mt-12">
          <Trans i18nKey="commons:login.message.invalidApprover" />
        </div>
      ) : (
        !loginResult &&
        loginError && <div className="login-fail__message mg mt-12">{loginError.message}</div>
      )}
      <Button
        fullWidth
        justifyContent={'center'}
        color="primary"
        classes={loginResult ? 'mg mt-32' : 'mg mt-16'}
        text={t('commons:login.$')}
        ref={submitButtonRef}
        onClick={submitSignIn}
      />
    </form>
  );
};

export default LoginForm;
